/** @jsxImportSource @emotion/react */
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import {
  ConnectWallet,
  EnableToken,
  FormikSubmitButton,
  FormikUpdateTokenTextField,
  LabeledInlineContent,
  Spinner,
} from 'components';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'translation';
import { convertTokensToWei, convertWeiToTokens, getContractAddress } from 'utilities';

import {
  useGetBalanceOf,
  useGetInviter,
  useGetMintableVai,
  useGetUmiPrice,
  useMintUmi,
  useGetHKTPrice,
} from 'clients/api';
import PLACEHOLDER_KEY from 'constants/placeholderKey';
import { MAINNET_PANCAKE_SWAP_TOKENS, PANCAKE_SWAP_TOKENS, TESTNET_PANCAKE_SWAP_TOKENS, TOKENS } from 'constants/tokens';
import { AmountForm, AmountFormProps } from 'containers/AmountForm';
import { AuthContext } from 'context/AuthContext';
import useConvertWeiToReadableTokenString from 'hooks/useConvertWeiToReadableTokenString';
import useHandleTransactionMutation from 'hooks/useHandleTransactionMutation';
import { Typography } from '@mui/material';
import { useStyles } from '../styles';
import getReadableFeeVai from './getReadableFeeVai';
import { VError } from '../../../../errors';
import { FormValues } from '../../../Swap/types';
import config from '../../../../config';

const vaiUnitrollerContractAddress = getContractAddress('dlVault');

const initialFormValues: FormValues = {
  fromToken: PANCAKE_SWAP_TOKENS.busd,
  fromTokenAmountTokens: '',
  fromTokenAddress: '',
  toToken: config.isOnTestnet ? TESTNET_PANCAKE_SWAP_TOKENS.busd : MAINNET_PANCAKE_SWAP_TOKENS.dl,
  toTokenAddress: '',
  toTokenAmountTokens: '',
  direction: 'exactAmountIn',
};

export interface MintVaiUiProps {
  disabled: boolean;
  isMintVaiLoading: boolean;
  isInitialLoading: boolean;
  mintUmi: (value: BigNumber, umiValue: BigNumber) => Promise<{ success: boolean; txHash?: string; error?: string }>;
  calculateNewBalance: (initial: BigNumber, amount: BigNumber) => BigNumber;
  mintFeePercentage?: number;
}

export const MintVaiUi: React.FC<MintVaiUiProps> = ({
  disabled,
  isInitialLoading,
  mintFeePercentage,
  isMintVaiLoading,
  mintUmi,
  calculateNewBalance,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { account } = useContext(AuthContext);
  const accountAddress = account?.address;
  const handleTransactionMutation = useHandleTransactionMutation();

  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  const { data: userUmiBalanceData } = useGetBalanceOf(
      { accountAddress: accountAddress || '', token: TOKENS.dl },
      { enabled: !!accountAddress },
  );

  const { data: userUsdtBalanceData } = useGetBalanceOf(
      { accountAddress: accountAddress || '', token: TOKENS.usdt },
      { enabled: !!accountAddress },
  );

  const limitTokens = useMemo(
    () =>
        userUmiBalanceData ? convertWeiToTokens({ valueWei: userUmiBalanceData.balanceWei, token: TOKENS.dl }).toFixed() : '0',
    [userUmiBalanceData?.balanceWei.toFixed()],
  );

  const limitBnbTokens = useMemo(
      () =>
          userUsdtBalanceData ? convertWeiToTokens(
              { valueWei: userUsdtBalanceData.balanceWei, token: TOKENS.dl }).toFixed() : '0',
      [userUsdtBalanceData?.balanceWei.toFixed()],
  );

  // Convert limit into VAI
  const readableUmiLimit = useConvertWeiToReadableTokenString({
    valueWei: userUmiBalanceData?.balanceWei,
    token: TOKENS.dl,
  });

  const readableBnbLimit = useConvertWeiToReadableTokenString({
    valueWei: userUsdtBalanceData?.balanceWei,
    token: TOKENS.usdt,
  });

  const hasMintableUmi = userUmiBalanceData?.balanceWei.isGreaterThan(0) || false;
  const hasMintableBnb = userUsdtBalanceData?.balanceWei.isGreaterThan(0) || false;

  const getReadableMintFee = useCallback(
    (valueWei: string) => {
      if (!mintFeePercentage) {
        return PLACEHOLDER_KEY;
      }

      const readableFeeVai = getReadableFeeVai({
        valueWei: new BigNumber(valueWei || 0),
        mintFeePercentage,
      });
      return `${readableFeeVai} (${mintFeePercentage}%)`;
    },
    [mintFeePercentage],
  );

  const { data: hktPriceData } = useGetHKTPrice(
      { accountAddress: accountAddress || '' },
      { enabled: !!accountAddress },
  );

  const hktPrice = hktPriceData ? hktPriceData.hktPrice.dividedBy(new BigNumber(10).pow(18)) : new BigNumber(1);


  const hypotheticalBnbToken = (value: any) => {
    if (!value || value === '') return 0;
    const numValue = parseFloat(value);
    if (Number.isNaN(numValue)) return 0;

    const hktPriceValue = hktPrice.toNumber();
    // 修正计算逻辑：USDT数量 ÷ HKT价格 = DL数量
    const result = hktPriceValue > 0 ? numValue / hktPriceValue : 0;

    console.log(`USDT -> DL: ${numValue} USDT ÷ ${hktPriceValue} = ${result} DL`);
    return result;
  };

  const hypotheticalUmiToken = (value: any) => {
    if (!value || value === '') return 0;
    const numValue = parseFloat(value);
    if (Number.isNaN(numValue)) return 0;

    const hktPriceValue = hktPrice.toNumber();
    // 修正计算逻辑：DL数量 × HKT价格 = USDT数量
    const result = hktPriceValue * numValue;

    console.log(`DL -> USDT: ${numValue} DL × ${hktPriceValue} = ${result} USDT`);
    return result;
  };

  const onSubmit: AmountFormProps['onSubmit'] = async (amountTokens, umiAmount) => {
    const amountWei = convertTokensToWei({
      value: new BigNumber(amountTokens),
      token: TOKENS.usdt,
    });

    const umiamountWei = convertTokensToWei({
      value: new BigNumber(umiAmount),
      token: TOKENS.dl,
    });

    try {
      await handleTransactionMutation({
        mutate: async () => {
          const result = await mintUmi(amountWei, umiamountWei);
          if (result.success && result.txHash) {
            return { transactionHash: result.txHash } as any;
          }
          throw new Error(result.error || 'Transaction failed');
        },
        successTransactionModalProps: transactionReceipt => ({
          title: t('mintRepayVai.mintVai.successfulTransactionModal.title'),
          content: t('mintRepayVai.mintVai.successfulTransactionModal.message'),
          amount: {
            valueWei: amountWei,
            token: TOKENS.dl,
          },
          transactionHash: transactionReceipt.transactionHash,
        }),
      });

      // 成功后重置表单
      setFormValues(initialFormValues);
    } catch (error) {
      // 错误已由 handleTransactionMutation 处理
      console.error('Transaction failed:', error);
    }
  };

  const newBalance = (value: any) => {
    const amount = calculateNewBalance(new BigNumber(0), new BigNumber(value));
    return amount;
  };

  return (
    <ConnectWallet message={t('mintRepayVai.mintVai.connectWallet')}>
      <EnableToken
        title={t('mintRepayVai.mintVai.enableToken')}
        token={TOKENS.dl}
        spenderAddress={vaiUnitrollerContractAddress}
      >
        <EnableToken
          title={t('mintRepayVai.mintVai.enableUsdtToken')}
          token={TOKENS.usdt}
          spenderAddress={vaiUnitrollerContractAddress}
        >
          {isInitialLoading ? (
            <Spinner />
          ) : (
            <AmountForm onSubmit={onSubmit} css={styles.tabContentContainer} bnbAmount={formValues.fromTokenAmountTokens} umiAmount={formValues.toTokenAmountTokens}>
              {({ values }) => (
                <>
                  <div css={styles.ctaContainer}>
                    <FormikUpdateTokenTextField
                      name="amount"
                      css={styles.textField}
                      token={TOKENS.usdt}
                      max={limitBnbTokens}
                      update={values.umiAmount ? newBalance(hypotheticalBnbToken(values.umiAmount)) : undefined}
                      disabled={disabled || !hasMintableUmi}
                      value={formValues.fromTokenAmountTokens}
                      onChange={amount =>
                          setFormValues(currentFormValues => ({
                            ...currentFormValues,
                            fromTokenAmountTokens: amount,
                            // Reset toTokenAmount field value if users resets fromTokenAmount
                            // field value
                            toTokenAmountTokens:
                                amount === ''
                                    ? initialFormValues.toTokenAmountTokens
                                    : (hypotheticalBnbToken(amount)).toString(),
                            direction: 'exactAmountIn',
                          }))
                      }
                      rightMaxButton={{
                        label: t('mintRepayVai.mintVai.rightMaxButtonLabel'),
                        valueOnClick: limitBnbTokens,
                      }}
                    />
                    <LabeledInlineContent
                      css={styles.getRow({ isLast: false })}
                      iconSrc={TOKENS.usdt}
                      label={t('mintRepayVai.mintVai.vaiLimitLabelBNB')}
                    >
                      {readableBnbLimit}
                    </LabeledInlineContent>

                    <FormikUpdateTokenTextField
                      name="umiAmount"
                      css={styles.textField}
                      token={TOKENS.dl}
                      max={limitTokens}
                      value={formValues.toTokenAmountTokens}
                      onChange={amount =>
                          setFormValues(currentFormValues => ({
                            ...currentFormValues,
                            toTokenAmountTokens: amount,
                            // Reset fromTokenAmount field value if users resets toTokenAmount
                            // field value
                            fromTokenAmountTokens:
                                amount === ''
                                    ? initialFormValues.fromTokenAmountTokens
                                    : (hypotheticalUmiToken(amount)).toString(),
                            direction: 'exactAmountOut',
                          }))
                      }
                      update={values.amount ? newBalance(hypotheticalBnbToken(values.amount)) : undefined}
                      disabled={disabled || isMintVaiLoading || !hasMintableBnb}
                      rightMaxButton={{
                          label: t('mintRepayVai.mintVai.rightMaxButtonLabel'),
                          valueOnClick: limitTokens,
                        }}
                    />

                    <LabeledInlineContent
                      css={styles.getRow({ isLast: false })}
                      iconSrc={TOKENS.dl}
                      label={t('mintRepayVai.mintVai.vaiLimitLabel')}
                    >
                      {readableUmiLimit}
                    </LabeledInlineContent>
                  </div>

                  <FormikSubmitButton
                    loading={isMintVaiLoading}
                    disabled={disabled}
                    fullWidth
                    variant="secondary"
                    enabledLabel={t('mintRepayVai.mintVai.btnMintVai')}
                  />
                </>
              )}
            </AmountForm>
          )}
        </EnableToken>
      </EnableToken>
    </ConnectWallet>
  );
};

const MintVai: React.FC = () => {
  const { account } = useContext(AuthContext);

  const { mutateAsync: contractMintVai, isLoading: isMintVaiLoading } = useMintUmi();

  const { data: InviterReward } = useGetInviter(
      {
        accountAddress: account?.address || '',
      },
      {
        enabled: !!account?.address,
      },
  );

  const mintUmi: MintVaiUiProps['mintUmi'] = async (amountWei, umiamountWei) => {
    if (!account) {
      throw new VError({ type: 'unexpected', code: 'undefinedAccountErrorMessage' });
    }

    const provider = new ethers.providers.Web3Provider(window.ethereum as any);
    const signer = provider.getSigner();

    return contractMintVai({
      userSigner: signer,
      amountWei,
      umiamountWei,
    });
  };

  return (
    <MintVaiUi
      disabled={!account}
      isInitialLoading={isMintVaiLoading}
      isMintVaiLoading={isMintVaiLoading}
      mintUmi={mintUmi}
      calculateNewBalance={(initial: BigNumber, amount: BigNumber) => initial.plus(amount)}
    />
  );
};

export default MintVai;
